import { Component, Input, OnInit } from '@angular/core';
import {
  LocalHourlyRate,
  Location,
  UserResponsiveness,
  UserStatus,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { FlagSize } from '@freelancer/ui/flag';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import {
  FontColor,
  FontWeight,
  TextAlign,
  TextTransform,
} from '@freelancer/ui/text';
import * as Rx from 'rxjs';
@Component({
  selector: 'app-user-profile-summary-information',
  template: `
    <fl-bit *ngIf="!isAgent && !isDeloitteDcUser" [flHideMobile]="true">
      <ng-container [ngTemplateOutlet]="hourlyRate"></ng-container>
    </fl-bit>

    <fl-bit
      *ngIf="isCorporate && responsiveness && (isLoggedIn$ | flAsync)"
      class="Row"
      [flMarginBottom]="Margin.XXSMALL"
    >
      <app-user-profile-summary-responsiveness
        [isOnline$]="isOnline$"
        [responsiveness]="responsiveness"
      ></app-user-profile-summary-responsiveness>
    </fl-bit>

    <fl-grid *ngIf="location && location?.country?.name">
      <fl-col class="SupplementaryInfo" [flMarginBottom]="Margin.XSMALL">
        <fl-bit [flMarginRight]="Margin.XSMALL">
          <fl-link
            *flFeature="Feature.DEPRECATED_SEARCH_PAGES; else showCountryFlag"
            flTrackingLabel="SummaryCountryLink"
            [link]="countryUrl"
          >
            <ng-container *ngTemplateOutlet="showCountryFlag"></ng-container>
          </fl-link>
        </fl-bit>

        <ng-template #showCountryFlag>
          <fl-flag
            [flHideMobile]="true"
            [country]="location?.country?.code"
          ></fl-flag>

          <fl-flag
            [flShowMobile]="true"
            [country]="location?.country?.code"
            [size]="FlagSize.SMALL"
          ></fl-flag>
        </ng-template>

        <fl-text
          [textTransform]="TextTransform.CAPITALIZE"
          [textAlign]="TextAlign.LEFT"
          i18n="User location label"
        >
          <ng-container *ngIf="location?.city">
            {{ location?.city?.toLowerCase() }},
          </ng-container>
          <fl-link
            *flFeature="Feature.DEPRECATED_SEARCH_PAGES; else countryTextNoLink"
            flTrackingLabel="SummaryCountryFlagIconClick"
            [link]="countryUrl"
          >
            {{ location?.country?.name?.toLowerCase() }}
          </fl-link>
          <ng-template #countryTextNoLink>
            {{ location?.country?.name?.toLowerCase() }}
          </ng-template>
        </fl-text>
      </fl-col>
    </fl-grid>

    <fl-bit *ngIf="!isAgent" [flHideTablet]="true" [flHideDesktop]="true">
      <ng-container [ngTemplateOutlet]="hourlyRate"></ng-container>
    </fl-bit>

    <ng-template #hourlyRate>
      <ng-container
        *ngIf="!(localHourlyRateLoading$ | flAsync); else loadingHourlyRate"
      >
        <ng-container *ngIf="localHourlyRate$ | flAsync as localHourlyRate">
          <fl-bit
            *ngIf="localHourlyRate.currency && !isCorporate"
            class="Row"
            [flMarginBottom]="Margin.XXSMALL"
          >
            <fl-text
              [textAlign]="TextAlign.LEFT"
              [weight]="FontWeight.BOLD"
              i18n="Hourly pay label"
            >
              <fl-icon
                [color]="IconColor.SUCCESS"
                [name]="'ui-receipt'"
                [size]="IconSize.MID"
                [flHideMobile]="true"
                [flMarginRight]="Margin.XXXSMALL"
              ></fl-icon>

              {{
                localHourlyRate.rate
                  | flCurrency: localHourlyRate.currency?.code:false:false
              }}
              / hour
            </fl-text>
          </fl-bit>

          <fl-bit
            *ngIf="isCorporate"
            class="Row Row--hourly"
            [flMarginBottom]="Margin.XXSMALL"
          >
            <fl-text [color]="FontColor.MID" i18n="Corporate hourly rate label">
              <fl-icon
                [size]="IconSize.MID"
                [color]="IconColor.SUCCESS"
                [name]="'ui-receipt'"
                [flHideMobile]="true"
              ></fl-icon>
              Our Rate
            </fl-text>
            <fl-text
              [textAlign]="TextAlign.RIGHT"
              [weight]="FontWeight.BOLD"
              i18n="Hourly pay label"
            >
              {{
                localHourlyRate.rate
                  | flCurrency: localHourlyRate.currency?.code:false:false
              }}
              / hour
            </fl-text>
          </fl-bit>
          <fl-hr *ngIf="isCorporate" [flMarginBottom]="Margin.XXSMALL"></fl-hr>
        </ng-container>
      </ng-container>
      <ng-template #loadingHourlyRate>
        <fl-bit class="Row">
          <fl-loading-text [padded]="false" [rows]="1"></fl-loading-text>
        </fl-bit>
      </ng-template>
    </ng-template>

    <app-user-profile-summary-verifications
      [status]="status"
      [flShowMobile]="true"
    ></app-user-profile-summary-verifications>
  `,
  styleUrls: ['./user-profile-summary-information.component.scss'],
})
export class UserProfileSummaryInformationComponent implements OnInit {
  FlagSize = FlagSize;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  Margin = Margin;
  TextAlign = TextAlign;
  TextTransform = TextTransform;
  Feature = Feature;

  @Input() isAgent: boolean;
  @Input() isCorporate: boolean;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isOnline$: Rx.Observable<boolean>;
  @Input() localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  @Input() localHourlyRateLoading$: Rx.Observable<boolean>;
  @Input() location?: Location;
  @Input() responsiveness?: UserResponsiveness;
  @Input() isDeloitteDcUser?: boolean;
  @Input() status: UserStatus;

  countryUrl: string | undefined;

  ngOnInit(): void {
    // This replicates the directory seo url found in
    // \DirectoryUtil::getSeoUrlFromString
    this.countryUrl = this.location
      ? this.location.country
        ? `/freelancers/${this.location.country.name
            .toLowerCase()
            .replace(/[^a-zA-Z ]/g, '')
            .replace(/[^a-zA-Z]/g, '-')}`
        : undefined
      : undefined;
  }
}
